import { useCallback, useMemo, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { GET_ALL_ENUM_METADATA, GET_RATIONALE_OPTIONS, LIST_APP_CONFIG } from '../queries/admin';
/* The fund-house register already had a document; a second one with the same
   operation name would make every `refetchQueries: ['GetAmcs']` fire twice. */
import { GET_AMCS } from '../queries/amcs';
import {
  DELETE_APP_CONFIG, REFRESH_ASSET_MODEL_CACHE, REFRESH_MARKET_CONSTANTS_CACHE,
  REFRESH_OPTIMIZER_PROFILE_CACHE, REFRESH_RATIONALE_CONFIG, RELOAD_APP_CONFIG,
  DELETE_ASSET_MODEL_COMPLETE, RECOMPUTE_REALIZED_CHARACTERISTICS,
  RUN_ASSET_MODEL_RESEARCH_AND_REPORT, UPDATE_ASSET_MODEL_COMPLETE, SEED_APP_CONFIG_FROM_DEFAULTS, SEED_SEBI_CATEGORY_MAPPING, SET_RATIONALE_ACTIVE, UPDATE_AMC,
  UPLOAD_AMC_LOGO,
  UPSERT_APP_CONFIG, WARM_CATALOG_CACHE,
} from '../mutations/admin';
import { GOALS_API_URL } from '../../config/backend';
import { ASSET_MODEL_ICON } from '../queries/models';
import { SYNC_ASSET_MODEL, UPLOAD_ASSET_MODEL_ICON } from '../mutations/models';


/* ── app_config ─────────────────────────────────────────────────────────── */

/**
 * Every configuration row the backend boots from.
 *
 * Grouped by environment here rather than on screen, because that is the axis
 * mistakes happen on: the same key exists per environment and editing the
 * wrong one changes a server nobody was looking at.
 */
export function useAppConfig() {
  const { data, loading, error, refetch } = useQuery(LIST_APP_CONFIG, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  return useMemo(() => {
    const rows = data?.listAppConfig ?? [];
    const environments = [...new Set(rows.map((r) => r.environment).filter(Boolean))].sort();
    const kinds = [...new Set(rows.map((r) => r.kind).filter(Boolean))].sort();
    return { rows, environments, kinds, loading, error, refetch };
  }, [data, loading, error, refetch]);
}

export const useUpsertAppConfig = () => useMutation(UPSERT_APP_CONFIG, { refetchQueries: ['ListAppConfig'] });
export const useDeleteAppConfig = () => useMutation(DELETE_APP_CONFIG, { refetchQueries: ['ListAppConfig'] });
export const useSeedAppConfig = () => useMutation(SEED_APP_CONFIG_FROM_DEFAULTS, { refetchQueries: ['ListAppConfig'] });
export const useSeedSebiCategoryMapping = () => useMutation(SEED_SEBI_CATEGORY_MAPPING, {
  refetchQueries: ['GetAllEnumMetadata'],
});

/* ── fund houses ────────────────────────────────────────────────────────── */

export function useAdminAmcs() {
  const { data, loading, error, refetch } = useQuery(GET_AMCS, {
    variables: { empanelledOnly: false },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  return { rows: data?.getAmcs ?? [], loading, error, refetch };
}

export const useUpdateAmc = () => useMutation(UPDATE_AMC, { refetchQueries: ['GetAmcs'] });
export const useUploadAmcLogo = () => useMutation(UPLOAD_AMC_LOGO, { refetchQueries: ['GetAmcs'] });

/* ── enums ──────────────────────────────────────────────────────────────── */

/**
 * Every server-driven enum, already grouped by enum name.
 *
 * These are the labels the whole platform reads at runtime — the KYC forms,
 * the client screens, the agent prompts — so this screen is where a wrong word
 * anywhere in the product gets fixed.
 */
export function useEnumMetadata() {
  const { data, loading, error, refetch } = useQuery(GET_ALL_ENUM_METADATA, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  return useMemo(() => {
    const rows = data?.getAllEnumMetadata ?? [];
    const byName = new Map();
    rows.forEach((r) => {
      if (!byName.has(r.enumName)) byName.set(r.enumName, []);
      byName.get(r.enumName).push(r);
    });
    return {
      rows,
      groups: [...byName.entries()]
        .map(([name, values]) => ({ name, values }))
        .sort((a, b) => a.name.localeCompare(b.name)),
      loading,
      error,
      refetch,
    };
  }, [data, loading, error, refetch]);
}

/* ── investment rationales ──────────────────────────────────────────────── */

/** The asset types the rationale set is keyed by, as the backend names them. */
export const RATIONALE_ASSET_TYPES = ['MUTUAL_FUND', 'STOCK', 'ETF'];

export function useRationaleOptions(assetType) {
  const { data, loading, error, refetch } = useQuery(GET_RATIONALE_OPTIONS, {
    variables: { assetType },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const payload = data?.getInvestmentRationaleOptions ?? null;
  return {
    horizons: payload?.horizons ?? [],
    refused: payload && payload.success === false ? (payload.error || 'refused') : null,
    loading,
    error,
    refetch,
  };
}

export const useSetRationaleActive = () => useMutation(SET_RATIONALE_ACTIVE, {
  refetchQueries: ['GetInvestmentRationaleOptions'],
});
export const useRefreshRationaleConfig = () => useMutation(REFRESH_RATIONALE_CONFIG, {
  refetchQueries: ['GetInvestmentRationaleOptions'],
});

/* ── caches ─────────────────────────────────────────────────────────────── */

/* Two of these live on the goals schema rather than the main one. */
const GOALS_CTX = { uri: GOALS_API_URL };

/** A JSON-scalar payload, which may be a bag or a bare message. */
const jsonRead = (field) => (data) => {
  const v = data?.[field];
  if (v == null) return { success: false, message: 'No response.' };
  if (typeof v === 'object') return { success: v.success !== false, message: v.message || JSON.stringify(v) };
  return { success: true, message: String(v) };
};
const typedRead = (field) => (data) => ({
  success: Boolean(data?.[field]?.success),
  message: data?.[field]?.message || '',
});

/**
 * Every cache the console can rebuild, in the order that makes sense: build
 * the expensive catalogs first, then drop the cheap lookups sitting in front
 * of them. One list, so "warm everything" and the individual buttons can never
 * disagree about what everything is.
 */
export const CACHE_OPS = [
  {
    key: 'catalog',
    label: 'Catalog caches',
    description: 'Rebuilds what sits behind the model and scheme catalogues — the expensive one, and the only op here that builds rather than drops.',
    doc: WARM_CATALOG_CACHE,
    variables: { scope: 'all', wait: false },
    context: GOALS_CTX,
    read: jsonRead('warmCatalogCache'),
  },
  {
    key: 'assetModels',
    label: 'Asset-model & simulation grid',
    description: 'Reloads the parsed allocations and the precomputed grid. Needed after anything that rewrites models, expected returns or the grid.',
    doc: REFRESH_ASSET_MODEL_CACHE,
    context: GOALS_CTX,
    read: jsonRead('refreshAssetModelCache'),
  },
  {
    key: 'marketConstants',
    label: 'Market constants',
    description: 'Drops the cached constants so the next read re-loads them.',
    doc: REFRESH_MARKET_CONSTANTS_CACHE,
    read: typedRead('refreshMarketConstantsCache'),
  },
  {
    key: 'optimizerProfiles',
    label: 'Optimizer profiles',
    description: 'Drops the cached profiles so the next ranking re-reads them.',
    doc: REFRESH_OPTIMIZER_PROFILE_CACHE,
    read: typedRead('refreshOptimizerProfileCache'),
  },
  {
    key: 'appConfig',
    label: 'App config',
    description: 'Re-reads the app_config table into the running process. Editing a row does not affect a running server until this runs.',
    doc: RELOAD_APP_CONFIG,
    read: jsonRead('reloadAppConfig'),
  },
];

/**
 * Running the cache ops.
 *
 * SEQUENTIALLY, never in parallel: these rebuild overlapping data on a
 * single-worker API, and firing five at once turns a warm-up into an outage.
 * The cost is a few seconds of waiting, which the caller shows.
 */
export function useCacheOps() {
  const client = useApolloClient();
  const [results, setResults] = useState({});
  const [busy, setBusy] = useState(null);
  const [runningAll, setRunningAll] = useState(false);

  const runOne = useCallback(async (op) => {
    setBusy(op.key);
    setResults((r) => ({ ...r, [op.key]: { running: true } }));
    try {
      const { data } = await client.mutate({
        mutation: op.doc,
        variables: op.variables,
        context: op.context,
        fetchPolicy: 'no-cache',
      });
      const read = op.read(data);
      setResults((r) => ({ ...r, [op.key]: { ...read, running: false } }));
      return read;
    } catch (err) {
      const read = { success: false, message: err.message, running: false };
      setResults((r) => ({ ...r, [op.key]: read }));
      return read;
    } finally {
      setBusy(null);
    }
  }, [client]);

  const runAll = useCallback(async () => {
    setRunningAll(true);
    /* eslint-disable no-await-in-loop -- sequential is the point; see above. */
    for (const op of CACHE_OPS) await runOne(op);
    /* eslint-enable no-await-in-loop */
    setRunningAll(false);
  }, [runOne]);

  return { results, busy, runningAll, runOne, runAll };
}

/* ── models ─────────────────────────────────────────────────────────────── */

/**
 * The delete mutation, for `deleteAssetModels` in lib/deleteAssetModel.js to
 * drive. Not called directly: the outcome needs reading per model, and
 * `success` alone would report a refused delete as done.
 */
export const useDeleteAssetModels = () => useMutation(DELETE_ASSET_MODEL_COMPLETE, {
  refetchQueries: ['GetCompleteAssetModels', 'GetAssetModelsWithFunds', 'GetAssetModelCatalogue'],
});

/** The queries a model edit invalidates, wherever it is made. */
const MODEL_READS = ['GetCompleteAssetModels', 'GetAssetModelsWithFunds', 'GetAssetModelCatalogue', 'GetAssetModelDetail'];

export const useUpdateAssetModel = () => useMutation(UPDATE_ASSET_MODEL_COMPLETE, {
  refetchQueries: MODEL_READS,
});

/**
 * Re-sync one model's implementation — the funds that fill each slice.
 *
 * `run(modelId)` resolves with the backend's message and throws when the sync
 * reports `success: false`, so a refused sync never reads as done.
 * `includeEtfs` is non-null on the resolver, so it is always sent.
 */
export function useSyncAssetModel() {
  const [mutate] = useMutation(SYNC_ASSET_MODEL, { refetchQueries: MODEL_READS });
  const [syncing, setSyncing] = useState(() => new Set());

  const run = useCallback(async (modelId, { includeEtfs = false } = {}) => {
    const id = Number(modelId);
    setSyncing((s) => new Set(s).add(id));
    try {
      const { data } = await mutate({ variables: { modelId: id, includeEtfs } });
      const res = data?.syncAssetModel;
      if (!res?.success) throw new Error(res?.message || `Model ${id} could not be synced`);
      return res.message || `Model ${id} synced`;
    } finally {
      setSyncing((s) => {
        const next = new Set(s);
        next.delete(id);
        return next;
      });
    }
  }, [mutate]);

  return { run, isSyncing: (modelId) => syncing.has(Number(modelId)) };
}

/* How many single-model syncs a bulk sync keeps in flight at once. */
const BULK_SYNC_CONCURRENCY = 3;

/**
 * Re-sync a given set of models — the register's "Sync all", which syncs the
 * models its filter is showing. `syncAllAssetModels` cannot be narrowed to
 * listed or unlisted, so this runs `syncAssetModel` per model, a few at a
 * time, and re-reads the model lists once at the end rather than per model.
 *
 *   run(models)  resolves to `{ synced, failed: [{ id, name, message }] }`.
 *   progress     `{ done, total }` while a run is going, else null.
 */
export function useSyncAssetModels() {
  const [mutate] = useMutation(SYNC_ASSET_MODEL);
  const client = useApolloClient();
  const [progress, setProgress] = useState(null);

  const run = useCallback(async (models, { includeEtfs = false } = {}) => {
    const queue = [...models];
    const failed = [];
    let synced = 0;
    let done = 0;
    setProgress({ done: 0, total: queue.length });
    const worker = async () => {
      while (queue.length) {
        const m = queue.shift();
        try {
          const { data } = await mutate({ variables: { modelId: Number(m.id), includeEtfs } });
          const res = data?.syncAssetModel;
          if (res?.success) synced += 1;
          else failed.push({ id: m.id, name: m.name, message: res?.message || 'not synced' });
        } catch (err) {
          failed.push({ id: m.id, name: m.name, message: err.message });
        }
        done += 1;
        setProgress({ done, total: models.length });
      }
    };
    try {
      await Promise.all(Array.from({ length: Math.min(BULK_SYNC_CONCURRENCY, queue.length) }, worker));
      await client.refetchQueries({ include: MODEL_READS });
    } finally {
      setProgress(null);
    }
    return { synced, failed };
  }, [mutate, client]);

  return { run, progress, running: progress != null };
}

/**
 * A model's icon URL, or null.
 *
 * Its own query (see ASSET_MODEL_ICON) so a server without `iconUrl` yet loses
 * only this. `unsupported` says that is what happened, so the upload control
 * can explain itself rather than fail on press.
 */
export function useModelIcon(modelId, { skip = false } = {}) {
  const { data, loading, error, refetch } = useQuery(ASSET_MODEL_ICON, {
    variables: { id: Number(modelId) },
    skip: skip || modelId == null,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const unsupported = Boolean(error) && /iconUrl|Cannot query field/i.test(error.message || '');
  return { url: data?.getAssetModelById?.iconUrl ?? null, loading, error, unsupported, refetch };
}

/** Upload a model's icon; re-reads the icon and the model reads afterwards. */
export const useUploadModelIcon = () => useMutation(UPLOAD_ASSET_MODEL_ICON, {
  refetchQueries: ['AssetModelIcon', ...MODEL_READS],
});

/** `persist: false` is a dry run — computed and reported, nothing written. */
export const useRecomputeRealized = () => useMutation(RECOMPUTE_REALIZED_CHARACTERISTICS, {
  refetchQueries: MODEL_READS,
});

export const useRunModelResearch = () => useMutation(RUN_ASSET_MODEL_RESEARCH_AND_REPORT);
